import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowUpRightIcon, ClipboardCopyIcon, RefreshCwIcon } from 'lucide-react'
import {
  alpha,
  Box,
  Grid,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from '@mui/material'

import { Button, Card, Skeleton } from '@repo/ui'
import { Search, SelectField } from 'components'
import { AnalyticsBlock } from 'components/display/Analytics'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ImageGalleryModal } from 'components/feedback/modals/ImageGalleryModal'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useSeoGsc, useSeoPageMeta, useWebsitePages } from 'hooks/api'
import type { PageMetaResponse, PageMetaRow } from 'services'
import { toCsvBody } from 'utils/csv'

import {
  DEFAULT_RANGE_PRESET,
  RANGE_PRESETS,
  RANGE_STORAGE_KEY,
  initialRangePreset,
  rangeForPreset,
  type RangePreset,
} from '../../websiteAnalyticsModel'
import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, dateTime, useTone } from '../shared'
import { fmt, fmtPct, fmtPos } from './seoModel'
import {
  META_SOURCE_URL,
  PAGE_META_ROUTE_PARAM,
  normalizePath,
  searchByPath,
  sortByKey,
  unclickedImpressions,
  visitsByPath,
  type SearchTotals,
  type SortKey,
  type VisitTotals,
} from './seoPageMetaModel'

/*
  VRG-695 — "Page titles & link previews".

  Every page in vera.inc's LIVE sitemap, with what search engines and chat apps read off it,
  joined to how much traffic each page gets. The API fetches the site on demand (5-minute
  cache); nothing is stored, so this can never show yesterday's copy. The copy is edited in
  vera-website only: content/meta.ts (title, description), content/share.ts and lib/og.tsx
  (link previews).

  Traffic is read through the SAME hooks the sibling surfaces use, never a new query:
    Visits         useWebsitePages — the Analytics → Website → Pages table (Vercel)
    Search Console useSeoGsc       — the Search Console section's page totals (gsc_daily,
                                     page-level rows, query = '')
  Each is gated by its own permission. Without `internal_analytics_view` the Visits column
  says so rather than this page reading analytics on the GTM grant.
*/

type ColumnId = 'visits' | 'search' | 'serp' | 'link' | 'image'
type Show = 'all' | 'flagged' | 'errors'
type GscPreset = '7d' | '28d' | '90d'

const COLUMNS: ReadonlyArray<{ id: ColumnId; label: string; term?: string }> = [
  { id: 'visits', label: 'Visits' },
  { id: 'search', label: 'Search Console' },
  { id: 'serp', label: 'Title and description', term: 'seo-page-title' },
  { id: 'link', label: 'Link title and description', term: 'seo-link-preview' },
  { id: 'image', label: 'Link preview image', term: 'seo-link-preview-image' },
]
const ALL_COLUMNS = COLUMNS.map((c) => c.id)

const SORTS: ReadonlyArray<{ value: SortKey; label: string }> = [
  { value: 'route', label: 'Route' },
  { value: 'visitors', label: 'Visitors' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'clicks', label: 'Clicks' },
  { value: 'ctr', label: 'CTR' },
  { value: 'position', label: 'Position' },
  { value: 'unclicked', label: 'Impressions, low CTR' },
]

const GSC_PRESETS: ReadonlyArray<{ value: GscPreset; label: string; days: number }> = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '28d', label: '28 days', days: 28 },
  { value: '90d', label: '90 days', days: 90 },
]

const isoDay = (d: Date) => d.toISOString().slice(0, 10)
const gscRange = (preset: GscPreset, now = new Date()) => {
  const days = GSC_PRESETS.find((p) => p.value === preset)?.days ?? 28
  const start = new Date(now.getTime())
  start.setUTCDate(start.getUTCDate() - (days - 1))
  return { since: isoDay(start), until: isoDay(now) }
}

/** The Website page's default: the viewer's last choice there, else 7 days. */
const visitsDefault = (): RangePreset => {
  try {
    return initialRangePreset(null, window.localStorage.getItem(RANGE_STORAGE_KEY))
  } catch {
    return DEFAULT_RANGE_PRESET
  }
}

const kb = (bytes: number) => `${fmt(Math.round(bytes / 1024))} KB`
const isFlagged = (r: PageMetaRow) =>
  !!(r.error || r.flags.title || r.flags.description || r.flags.image)

/** Why a count is flagged, in the website guard's own terms. */
const flagText = (
  kind: 'title' | 'description',
  flag: string | null,
  t: PageMetaResponse['thresholds'],
): string | null => {
  if (!flag) return null
  if (flag === 'missing') return kind === 'title' ? 'No <title> on the page.' : 'No meta description on the page.'
  if (kind === 'title') return `Over ${t.titleMaxChars} characters: Google cuts the end off.`
  return flag === 'short'
    ? `Under ${t.descriptionMinChars} characters: reads as a fragment.`
    : `Over ${t.descriptionMaxChars} characters: the snippet is cut mid-sentence.`
}

const Count = ({ count, flag }: { count: number | null; flag: string | null }) => {
  const tone = useTone()
  return (
    <Typography
      component="div"
      variant="body3"
      sx={{
        mt: '2px',
        mb: 1,
        fontSize: 11.5,
        fontWeight: flag ? 600 : 400,
        color: flag ? tone(count === null ? 'negative' : 'caution').color : 'text.secondary',
      }}
    >
      {count === null ? 'Missing' : `${count} characters`}
      {flag ? <InfoTooltip text={flag} /> : null}
    </Typography>
  )
}

/** Long copy wraps inside a bounded column; the house `Cell` is nowrap for figures. */
const Wrap = ({ children, min = 150, max = 260 }: { children: React.ReactNode; min?: number; max?: number }) => (
  <Box sx={{ whiteSpace: 'normal', minWidth: min, maxWidth: max, lineHeight: 1.45 }}>{children}</Box>
)

const dash = <span style={{ opacity: 0.6 }}>—</span>

const ImageCell = ({ row, maxBytes, onOpen }: { row: PageMetaRow; maxBytes: number; onOpen: () => void }) => {
  const tone = useTone()
  const heavy = row.flags.image === 'heavy'
  return (
    <Cell>
      <Stack spacing={1} sx={{ width: 140 }}>
        {row.ogImage ? (
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            aria-label={`Enlarge the link preview image for ${row.route}`}
            sx={{
              p: 0,
              display: 'block',
              width: '100%',
              // The crop every major chat app uses for a summary_large_image card.
              aspectRatio: '1.91 / 1',
              overflow: 'hidden',
              borderRadius: '6px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              cursor: 'zoom-in',
              '&:hover, &:focus-visible': { borderColor: 'text.secondary' },
            }}
          >
            <Box
              component="img"
              src={row.ogImage}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        ) : null}
        <Typography
          variant="body3"
          sx={{
            fontSize: 11.5,
            fontWeight: row.flags.image ? 600 : 400,
            color: row.flags.image ? tone(heavy ? 'caution' : 'negative').color : 'text.secondary',
            whiteSpace: 'normal',
          }}
        >
          {row.flags.image === 'missing'
            ? 'No og:image'
            : row.imageError
              ? row.imageError
              : row.imageBytes !== null
                ? `${row.imageBytesAtLeast ? 'At least ' : ''}${kb(row.imageBytes)}`
                : 'Size unknown'}
          {heavy ? <InfoTooltip text={`Over ${kb(maxBytes)}: WhatsApp shows the link without the image.`} /> : null}
        </Typography>
      </Stack>
    </Cell>
  )
}

const SearchCell = ({ s, synced }: { s: SearchTotals | undefined; synced: boolean }) => (
  <Cell align="right">
    {!synced ? (
      dash
    ) : !s ? (
      <Typography variant="body3" color="text.secondary" sx={{ fontSize: 12 }}>
        No impressions
      </Typography>
    ) : (
      <Stack spacing="2px" sx={{ fontSize: 12.5 }}>
        <span>
          <strong>{fmt(s.impressions)}</strong> impr. · <strong>{fmt(s.clicks)}</strong> clicks
        </span>
        <span style={{ opacity: 0.75 }}>
          CTR {fmtPct(s.ctr)} · pos. {fmtPos(s.position)}
        </span>
      </Stack>
    )}
  </Cell>
)

const CSV_HEADER = [
  'route',
  'title',
  'title_chars',
  'description',
  'description_chars',
  'og_title',
  'og_description',
  'og_image',
  'og_image_kb',
  'visitors',
  'pageviews',
  'gsc_impressions',
  'gsc_clicks',
  'gsc_ctr',
  'gsc_position',
  'flags',
  'error',
]

const toCsv = (
  rows: PageMetaRow[],
  visits: (r: PageMetaRow) => VisitTotals | undefined,
  search: (r: PageMetaRow) => SearchTotals | undefined,
): string =>
  toCsvBody([
    CSV_HEADER,
    ...rows.map((r) => {
      const v = visits(r)
      const s = search(r)
      const n = (x: number | null | undefined, digits?: number) =>
        x === null || x === undefined ? '' : digits === undefined ? String(x) : x.toFixed(digits)
      return [
        r.route,
        r.title ?? '',
        n(r.titleChars),
        r.description ?? '',
        n(r.descriptionChars),
        r.ogTitle ?? '',
        r.ogDescription ?? '',
        r.ogImage ?? '',
        r.imageBytes === null ? '' : (r.imageBytes / 1024).toFixed(1),
        n(v?.visitors),
        n(v?.pageviews),
        n(s?.impressions),
        n(s?.clicks),
        n(s?.ctr, 4),
        n(s?.position, 1),
        [
          r.flags.title && `title:${r.flags.title}`,
          r.flags.description && `description:${r.flags.description}`,
          r.flags.image && `image:${r.flags.image}`,
        ]
          .filter(Boolean)
          .join(' '),
        r.error ?? r.imageError ?? '',
      ]
    }),
  ])

const Tiles = ({ data }: { data: PageMetaResponse }) => (
  <Grid container spacing={4}>
    {(
      [
        ['Pages', undefined, data.counts.pages, `From ${data.sitemapUrl.replace(/^https?:\/\//, '')}`],
        ['Titles flagged', 'seo-page-title', data.counts.titlesFlagged, `Over ${data.thresholds.titleMaxChars} characters, or missing`],
        [
          'Descriptions flagged',
          'seo-meta-description',
          data.counts.descriptionsFlagged,
          `Outside ${data.thresholds.descriptionMinChars}–${data.thresholds.descriptionMaxChars} characters, or missing`,
        ],
        [
          'Images flagged',
          'seo-link-preview-image',
          data.counts.imagesFlagged,
          `Over ${kb(data.thresholds.imageMaxBytes)}, missing or unreadable`,
        ],
      ] as const
    ).map(([title, term, stat, unit]) => (
      <Grid item xs={12} sm={6} md={3} key={title}>
        <AnalyticsBlock title={title} term={term} stat={fmt(stat)} unit={unit} />
      </Grid>
    ))}
  </Grid>
)

export const SeoPageMetaCard = () => {
  const theme = useTheme()
  const [params, setParams] = useSearchParams()
  const { data, isLoading, error, refresh } = useSeoPageMeta()
  const { open: notify } = useSnackbar()
  const { isHasAccess: canSeeAnalytics } = useCanAccess({ action: 'internal_analytics_view' })

  const [query, setQuery] = useState('')
  const [show, setShow] = useState<Show>('all')
  const [columns, setColumns] = useState<ColumnId[]>(ALL_COLUMNS)
  const [sort, setSort] = useState<SortKey>('route')
  const [visitsPreset, setVisitsPreset] = useState<RangePreset>(visitsDefault)
  const [gscPreset, setGscPreset] = useState<GscPreset>('28d')
  const [enlarged, setEnlarged] = useState<PageMetaRow | null>(null)

  const visitsRange = useMemo(() => rangeForPreset(visitsPreset), [visitsPreset])
  const searchRange = useMemo(() => gscRange(gscPreset), [gscPreset])
  const pages = useWebsitePages(visitsRange, canSeeAnalytics)
  const gsc = useSeoGsc(searchRange, false)

  const visits = useMemo(() => visitsByPath(pages.data?.rows ?? []), [pages.data])
  const search = useMemo(() => searchByPath(gsc.data?.pages ?? []), [gsc.data])
  // "No data yet" is a STATE, not zeros: production has no rows until the VRG-669 load runs.
  const gscSynced = !!gsc.data?.lastSyncedAt
  const visitsOf = (r: PageMetaRow) => visits.get(normalizePath(r.route))
  const searchOf = (r: PageMetaRow) => search.get(normalizePath(r.route))

  // Deep link from Analytics → Website → Pages: `?route=/pricing` lands on that row.
  const focus = params.get(PAGE_META_ROUTE_PARAM)
  const focusPath = focus ? normalizePath(focus) : null
  const focusRef = useRef<HTMLTableRowElement | null>(null)
  useEffect(() => {
    if (focusPath && data && focusRef.current)
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [focusPath, data])
  const clearFocus = () => {
    const next = new URLSearchParams(params)
    next.delete(PAGE_META_ROUTE_PARAM)
    setParams(next, { replace: true })
  }

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = (data?.rows ?? [])
      .filter((r) => (show === 'flagged' ? isFlagged(r) : show === 'errors' ? !!(r.error || r.imageError) : true))
      .filter((r) =>
        q ? [r.route, r.title, r.description, r.ogTitle, r.ogDescription].some((v) => v?.toLowerCase().includes(q)) : true,
      )
    return sortByKey(filtered, sort, (r, key) => {
      if (key === 'visitors') return visits.get(normalizePath(r.route))?.visitors ?? null
      const s = search.get(normalizePath(r.route))
      if (key === 'unclicked') return unclickedImpressions(s)
      return s ? s[key] : null
    })
  }, [data, query, show, sort, visits, search])

  const on = (id: ColumnId) => columns.includes(id)
  const colSpan = 1 + columns.length
  const sortHeader = (label: string, key: SortKey) => (
    <TableSortLabel
      active={sort === key}
      direction={key === 'position' || key === 'route' ? 'asc' : 'desc'}
      onClick={() => setSort(sort === key ? 'route' : key)}
    >
      {label}
    </TableSortLabel>
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(toCsv(rows, visitsOf, searchOf))
      notify({ type: 'success', text: `Copied ${fmt(rows.length)} rows as CSV.` })
    } catch {
      notify({ type: 'error', text: 'Could not write to the clipboard.' })
    }
  }

  const brand = theme.palette.brand[500]
  const highlight = alpha(brand, theme.palette.mode === 'dark' ? 0.18 : 0.08)

  return (
    <Card
      title="Page titles & link previews"
      action={
        <Stack direction="row" spacing={3}>
          <Button
            variant="secondary"
            disabled={!data || rows.length === 0}
            onClick={copy}
            startIcon={<ClipboardCopyIcon size={16} strokeWidth={1.5} />}
          >
            Copy as CSV
          </Button>
          <Button
            variant="secondary"
            loading={refresh.isPending}
            disabled={isLoading || refresh.isPending}
            onClick={() =>
              refresh.mutate(undefined, {
                onSuccess: (r) =>
                  notify({
                    type: r.error ? 'error' : 'success',
                    text: r.error ?? `Read ${fmt(r.counts.pages)} pages from ${r.baseUrl.replace(/^https?:\/\//, '')}.`,
                  }),
                onError: (e) => notify({ type: 'error', text: (e as Error).message }),
              })
            }
            startIcon={<RefreshCwIcon size={16} strokeWidth={1.5} />}
          >
            Refresh
          </Button>
        </Stack>
      }
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <>
            <Grid container spacing={4}>
              {[0, 1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton height={96} />
                </Grid>
              ))}
            </Grid>
            <Skeleton height={14} lines={12} />
          </>
        ) : error || !data ? (
          <SectionNote>Could not load page titles: {(error as Error | null)?.message}</SectionNote>
        ) : (
          <>
            {data.error ? <Notice severity="error">{data.error}</Notice> : null}
            {data.warnings.map((w) => (
              <Notice key={w} severity="warning">
                {w}
              </Notice>
            ))}
            {gsc.data && !gscSynced ? (
              <Notice severity="info">
                No Search Console data yet. The Search Console column fills once the gsc-sync job has
                loaded this environment&rsquo;s history; until then it shows a dash, not zero.
              </Notice>
            ) : null}
            <Tiles data={data} />
            <SectionNote>
              Read live from {data.baseUrl.replace(/^https?:\/\//, '')} at {dateTime(data.fetchedAt)}
              {data.cached ? ' (cached; Refresh reads the site again)' : ` in ${(data.durationMs / 1000).toFixed(1)}s`}.
              Nothing here is stored or edited: search copy lives in the website&rsquo;s{' '}
              <code>content/meta.ts</code>, link previews in <code>content/share.ts</code> and <code>lib/og.tsx</code>.
              Character limits are the website build&rsquo;s own. Only{' '}
              {data.allowlist.filter((h) => !/localhost|127\.0\.0\.1|::1/.test(h)).join(', ')} are ever fetched.
              Visits are the Analytics Website figures (all visitors, Vercel); Search Console figures are
              Google&rsquo;s, two to three days behind.
            </SectionNote>
            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))', lg: '2fr repeat(5, minmax(0, 1fr))' },
              }}
            >
              <Search
                value={query}
                onChange={setQuery}
                placeholder="Search routes, titles and descriptions"
                sx={{ minWidth: 0, width: '100%' }}
              />
              <SelectField
                value={show}
                onChange={(v) => setShow(v as Show)}
                options={[
                  { value: 'all', label: `All pages (${fmt(data.counts.pages)})` },
                  { value: 'flagged', label: `Flagged only (${fmt(data.rows.filter(isFlagged).length)})` },
                  { value: 'errors', label: 'Read errors only' },
                ]}
              />
              <SelectField
                value={sort}
                onChange={(v) => setSort(v as SortKey)}
                renderTriggerValue={() => `Sort: ${SORTS.find((s) => s.value === sort)?.label}`}
                options={SORTS.map((s) => ({ value: s.value, label: s.label }))}
              />
              <SelectField
                value={visitsPreset}
                disabled={!canSeeAnalytics}
                onChange={(v) => setVisitsPreset(v as RangePreset)}
                renderTriggerValue={() => `Visits: ${RANGE_PRESETS.find((p) => p.value === visitsPreset)?.label}`}
                options={RANGE_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              />
              <SelectField
                value={gscPreset}
                onChange={(v) => setGscPreset(v as GscPreset)}
                renderTriggerValue={() => `Search: ${GSC_PRESETS.find((p) => p.value === gscPreset)?.label}`}
                options={GSC_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
              />
              <SelectField
                multiple
                value={columns}
                onChange={(v) => {
                  const next = v as ColumnId[]
                  // Keep the table's column order, whatever order they were ticked in.
                  setColumns(ALL_COLUMNS.filter((c) => next.includes(c)))
                }}
                renderTriggerValue={() => `Columns · ${columns.length} of ${ALL_COLUMNS.length}`}
                options={COLUMNS.map((c) => ({ value: c.id, label: c.label }))}
              />
            </Box>
            <SectionNote>
              {fmt(rows.length)} of {fmt(data.counts.pages)} pages, sorted by{' '}
              {SORTS.find((s) => s.value === sort)?.label.toLowerCase()}
              {sort === 'unclicked' ? ' (impressions that did not become a click)' : ''}. Click a preview
              image to see it full size.
              {focusPath ? (
                <>
                  {' '}
                  Showing <strong>{focusPath}</strong>{' '}
                  <Box component="button" type="button" onClick={clearFocus} sx={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline' }}>
                    clear
                  </Box>
                  .
                </>
              ) : null}
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>{sortHeader('Route', 'route')}</HeadCell>
                  {on('visits') ? (
                    <HeadCell align="right">
                      {sortHeader('Visits', 'visitors')}
                      <InfoTooltip text="Visitors to the page in the Visits range, all visitors (Vercel): the same figure as Analytics → Website → Pages." />
                    </HeadCell>
                  ) : null}
                  {on('search') ? (
                    <HeadCell align="right">
                      {sortHeader('Search Console', 'impressions')}
                      <InfoTooltip term="gsc-impressions" />
                    </HeadCell>
                  ) : null}
                  {on('serp') ? (
                    <HeadCell>
                      Title and description
                      <InfoTooltip term="seo-page-title" />
                    </HeadCell>
                  ) : null}
                  {on('link') ? (
                    <HeadCell>
                      Link title and description
                      <InfoTooltip term="seo-link-preview" />
                    </HeadCell>
                  ) : null}
                  {on('image') ? (
                    <HeadCell>
                      Link preview image
                      <InfoTooltip term="seo-link-preview-image" />
                    </HeadCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <EmptyRow colSpan={colSpan} text="No pages match these filters." />
                ) : (
                  rows.map((r) => {
                    const focused = focusPath === normalizePath(r.route)
                    const v = visitsOf(r)
                    return (
                      <TableRow
                        key={r.route}
                        ref={focused ? focusRef : undefined}
                        data-route={r.route}
                        sx={{
                          verticalAlign: 'top',
                          ...(focused ? { bgcolor: highlight, boxShadow: `inset 3px 0 0 ${brand}` } : {}),
                        }}
                      >
                        <Cell strong>
                          <Box sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 90, maxWidth: 150 }}>
                            <a href={r.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                              {r.route}
                            </a>
                            <Box
                              component="a"
                              href={META_SOURCE_URL}
                              target="_blank"
                              rel="noreferrer"
                              sx={{
                                display: 'block',
                                mt: 1,
                                fontSize: 11,
                                fontWeight: 400,
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              Edit in code:{' '}
                              <span style={{ whiteSpace: 'nowrap' }}>
                                content/meta.ts
                                <ArrowUpRightIcon size={11} strokeWidth={1.5} style={{ marginLeft: 2, verticalAlign: '-1px' }} />
                              </span>
                            </Box>
                          </Box>
                        </Cell>
                        {on('visits') ? (
                          <Cell align="right">
                            {!canSeeAnalytics ? (
                              <InfoTooltip text="Visits need the internal_analytics_view permission.">
                                <span style={{ opacity: 0.6, cursor: 'help' }}>—</span>
                              </InfoTooltip>
                            ) : v?.visitors !== null && v?.visitors !== undefined ? (
                              <Stack spacing="2px">
                                <strong>{fmt(v.visitors)}</strong>
                                <span style={{ opacity: 0.75, fontSize: 12 }}>{fmt(v.pageviews)} views</span>
                              </Stack>
                            ) : (
                              dash
                            )}
                          </Cell>
                        ) : null}
                        {on('search') ? <SearchCell s={searchOf(r)} synced={gscSynced} /> : null}
                        {r.error ? (
                          <TableCell colSpan={Math.max(1, columns.filter((c) => c !== 'visits' && c !== 'search').length)}>
                            <Notice severity="error">Could not read this page: {r.error}</Notice>
                          </TableCell>
                        ) : (
                          <>
                            {on('serp') ? (
                              <Cell>
                                <Wrap min={160} max={280}>
                                  <Box sx={{ fontWeight: 600 }}>{r.title ?? dash}</Box>
                                  <Count count={r.titleChars} flag={flagText('title', r.flags.title, data.thresholds)} />
                                  <Box>{r.description ?? dash}</Box>
                                  <Count
                                    count={r.descriptionChars}
                                    flag={flagText('description', r.flags.description, data.thresholds)}
                                  />
                                </Wrap>
                              </Cell>
                            ) : null}
                            {on('link') ? (
                              <Cell>
                                <Wrap min={140} max={240}>
                                  <Box sx={{ fontWeight: 600, mb: 1 }}>{r.ogTitle ?? dash}</Box>
                                  <Box sx={{ color: 'text.secondary' }}>{r.ogDescription ?? dash}</Box>
                                </Wrap>
                              </Cell>
                            ) : null}
                            {on('image') ? (
                              <ImageCell row={r} maxBytes={data.thresholds.imageMaxBytes} onOpen={() => setEnlarged(r)} />
                            ) : null}
                          </>
                        )}
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </StatTable>
          </>
        )}
      </Stack>
      <ImageGalleryModal
        open={!!enlarged?.ogImage}
        onClose={() => setEnlarged(null)}
        images={enlarged?.ogImage ? [enlarged.ogImage] : []}
        title={
          enlarged
            ? `Link preview: ${enlarged.route}${enlarged.imageBytes !== null ? ` · ${kb(enlarged.imageBytes)}` : ''}`
            : 'Link preview'
        }
      />
    </Card>
  )
}
